import { useState, useRef, useEffect } from "react";
import { lsGet } from "../utils/storage.js";
import { DIAL_CODES, getStatesForCountry, getLgasForState } from "../utils/phone.js";
import { getVoter } from "../services/auth.js";
import { supabase } from "../lib/supabase.js";
import { Logo } from "../atoms/Logo.jsx";
import { validateSignupEmail } from "../utils/emailValidation.js";

const NIGERIA = DIAL_CODES.find(d => d.code === "NG");

/**
 * EMAIL-4 + EMAIL-5: Email magic-link onboarding.
 *
 * Three-step flow:
 * 1. Email entry → send magic link via Supabase
 * 2. "Check your email" confirmation (waiting for magic link click)
 * 3. Location selection (state/LGA/gender) → onComplete callback
 *
 * Magic link callback handled by App.jsx route → onAuthStateChange triggers
 * automatic session detection → this component detects the session and proceeds.
 */
export function EmailOnboarding({ onComplete }) {
  const saved = lsGet("qm_voter", null);
  const savedCountry = saved?.countryCode ? DIAL_CODES.find(d => d.code === saved.countryCode) ?? NIGERIA : null;

  // EMAIL-5: Check for verified email from magic link callback.
  // Use useRef to cache the value across React StrictMode double-invocations
  // (StrictMode calls the render function twice and uses the second result;
  // reading + removing sessionStorage in the render body would lose the value
  // on the second invocation).
  const _verifiedRef = useRef(undefined);
  if (_verifiedRef.current === undefined) {
    _verifiedRef.current = sessionStorage.getItem("qm_verified_email") ?? "";
  }
  const verifiedEmail = _verifiedRef.current;

  const [email, setEmail] = useState(verifiedEmail || "");
  const [country, setCountry] = useState(savedCountry ?? NIGERIA);
  const [state, setState] = useState(saved?.state ?? "");
  const [lga, setLga] = useState(saved?.lga ?? "");
  const [gender, setGender] = useState(saved?.gender ?? "");
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [policyConsent, setPolicyConsent] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const emailRef = useRef(null);

  // Start at location step if: returning user with saved location, or magic link just verified
  const [step, setStep] = useState(() => {
    if (verifiedEmail) return "location"; // Post-magic-link → go straight to location
    if (saved?.state) return "location";
    return "email";
  });

  // Clean up the consumed sessionStorage flag after stable mount
  useEffect(() => {
    if (verifiedEmail) sessionStorage.removeItem("qm_verified_email");
  }, []);

  // ── Email step: send magic link ────────────────────────────────────
  const handleSendMagicLink = async () => {
    setErr("");
    const trimmed = email.trim().toLowerCase();
    if (!trimmed || !trimmed.includes("@")) {
      setErr("Please enter a valid email address.");
      return;
    }

    // AUTH-14: client-side pre-check mirrors the API blocklist so we reject
    // disposable/test addresses inline — before burning a Supabase magic-link
    // quota. API validation still runs on voter upsert; this is UX only.
    const blockedReason = validateSignupEmail(trimmed);
    if (blockedReason) {
      setErr(blockedReason);
      return;
    }

    setLoading(true);

    // Check for returning user with complete profile
    try {
      const existing = await getVoter(trimmed);
      if (existing?.state) {
        // Pre-fill location for returning users
        setState(existing.state);
        if (existing?.lga) setLga(existing.lga);
      }
    } catch { /* ignore — proceed without pre-fill */ }

    // Send magic link via Supabase
    try {
      const redirectUrl = `${window.location.origin}/auth/callback`;
      const { error } = await supabase.auth.signInWithOtp({
        email: trimmed,
        options: { emailRedirectTo: redirectUrl },
      });
      if (error) {
        // Rate limit — a previous email was already sent, so advance to
        // check-email and start cooldown instead of showing a raw error.
        if (error.code === "over_email_send_rate_limit" || error.message?.toLowerCase().includes("rate limit")) {
          setMagicLinkSent(true);
          setLoading(false);
          setStep("check-email");
          startResendCooldown();
          setErr("A magic link was already sent. Please check your inbox or wait to resend.");
          return;
        }
        throw error;
      }

      setMagicLinkSent(true);
      setLoading(false);
      setStep("check-email");
      startResendCooldown();
    } catch (e) {
      setLoading(false);
      setErr(e.message || "Failed to send magic link. Please try again.");
    }
  };

  const startResendCooldown = () => {
    setResendCooldown(60);
    const interval = setInterval(() => {
      setResendCooldown(prev => {
        if (prev <= 1) { clearInterval(interval); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    setErr("");
    setLoading(true);
    try {
      const redirectUrl = `${window.location.origin}/auth/callback`;
      const { error } = await supabase.auth.signInWithOtp({
        email: email.trim().toLowerCase(),
        options: { emailRedirectTo: redirectUrl },
      });
      if (error) {
        if (error.code === "over_email_send_rate_limit" || error.message?.toLowerCase().includes("rate limit")) {
          setLoading(false);
          startResendCooldown();
          setErr("Please wait a moment before requesting another link.");
          return;
        }
        throw error;
      }
      setLoading(false);
      startResendCooldown();
    } catch (e) {
      setLoading(false);
      setErr("Failed to resend. Please try again shortly.");
    }
  };

  // ── Location step ──────────────────────────────────────────────────
  const handleLocation = () => {
    if (!state) { setErr("Please select your " + country.stateLabel + "."); return; }
    if (!ageConfirmed) { setErr("You must confirm you are 18 or older to continue."); return; }
    if (!policyConsent) { setErr("You must agree to the Privacy Policy and Terms of Service to continue."); return; }
    onComplete({
      email: email.trim().toLowerCase(),
      countryCode: country.code,
      country: country.country,
      flag: country.flag,
      state,
      lga,
      gender: gender || undefined,
      stateLabel: country.stateLabel,
      lgaLabel: country.lgaLabel,
      consents: { ageConfirmed: true, policyConsent: true },
    });
  };

  const stateList = country ? getStatesForCountry(country.code) : [];
  const lgaList = (country && state) ? getLgasForState(country.code, state) : [];

  const F = { width:"100%", padding:"13px 16px", background:"#0d1724", border:"1px solid #1e2d45", borderRadius:11, color:"#e2e8f0", fontSize:16, fontFamily:"inherit", outline:"none", letterSpacing:"0.03em" };

  return (
    <div style={{minHeight:"100vh",background:"#090e1a",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:"24px 20px"}}>
      <div style={{width:"100%",maxWidth:400,animation:"fadeUp .4s ease"}}>

        {/* Logo */}
        <div style={{textAlign:"center",marginBottom:36}}>
          <div style={{margin:"0 auto 14px",display:"flex",justifyContent:"center"}}><Logo size={32} /></div>
          <p style={{color:"#475569",fontSize:13,marginTop:8,lineHeight:1.6}}>No PR team. No dodging. Just you and them.</p>
        </div>

        {/* ── Email entry step ──────────────────────────────────── */}
        {step === "email" && (
          <div style={{animation:"fadeUp .3s ease",position:"relative"}}>
            <div style={{marginBottom:28}}>
              <h2 style={{color:"#f1f5f9",fontSize:20,fontWeight:800,fontFamily:"Georgia,serif",margin:"0 0 6px"}}>Enter your email</h2>
              <p style={{color:"#64748b",fontSize:13,margin:0,lineHeight:1.6}}>We'll send you a magic link to sign in — no password needed.</p>
            </div>

            <div style={{marginBottom:16}}>
              <input
                ref={emailRef}
                type="email"
                inputMode="email"
                autoComplete="email"
                placeholder="you@example.com"
                value={email}
                onChange={e => { setEmail(e.target.value); setErr(""); }}
                onKeyDown={e => e.key === "Enter" && handleSendMagicLink()}
                style={F}
              />
            </div>

            {err && <div style={{marginBottom:12,color:"#ef4444",fontSize:12}}>{err}</div>}

            <button
              onClick={handleSendMagicLink}
              disabled={loading}
              style={{width:"100%",padding:"14px",background:"linear-gradient(135deg,#3b82f6,#2563eb)",color:"#fff",border:"none",borderRadius:11,fontSize:15,fontWeight:700,cursor:loading?"not-allowed":"pointer",fontFamily:"inherit",opacity:loading?.7:1}}
            >
              {loading ? "Sending link..." : "Send Magic Link"}
            </button>
          </div>
        )}

        {/* ── Check-email step ──────────────────────────────────── */}
        {step === "check-email" && (
          <div style={{animation:"fadeUp .3s ease",textAlign:"center"}}>
            <div style={{fontSize:48,marginBottom:20}}>
              <span role="img" aria-label="email">&#x2709;&#xFE0F;</span>
            </div>
            <h2 style={{color:"#f1f5f9",fontSize:20,fontWeight:800,fontFamily:"Georgia,serif",margin:"0 0 10px"}}>Check your email</h2>
            <p style={{color:"#64748b",fontSize:14,margin:"0 0 8px",lineHeight:1.6}}>
              We sent a magic link to
            </p>
            <p style={{color:"#60a5fa",fontSize:15,fontWeight:600,margin:"0 0 24px",wordBreak:"break-all"}}>
              {email.trim().toLowerCase()}
            </p>
            <p style={{color:"#475569",fontSize:13,margin:"0 0 28px",lineHeight:1.6}}>
              Click the link in the email to sign in. You can close this tab — the link will bring you back.
            </p>

            {err && <div style={{marginBottom:12,color:"#ef4444",fontSize:12}}>{err}</div>}

            <div style={{display:"flex",flexDirection:"column",gap:12}}>
              <button
                onClick={handleResend}
                disabled={loading || resendCooldown > 0}
                style={{width:"100%",padding:"12px",background:"transparent",color:"#3b82f6",border:"1px solid #1e3a5f",borderRadius:11,fontSize:14,fontWeight:600,cursor:(loading || resendCooldown > 0)?"not-allowed":"pointer",fontFamily:"inherit",opacity:(loading || resendCooldown > 0)?.6:1}}
              >
                {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Resend Magic Link"}
              </button>

              <button
                onClick={() => { setStep("email"); setMagicLinkSent(false); setErr(""); }}
                style={{background:"none",border:"none",color:"#64748b",cursor:"pointer",fontSize:13,fontFamily:"inherit",textDecoration:"underline"}}
              >
                Use a different email
              </button>
            </div>
          </div>
        )}

        {/* ── Location step ─────────────────────────────────────── */}
        {step === "location" && country && (
          <div style={{animation:"fadeUp .3s ease"}}>
            <div style={{marginBottom:24}}>
              <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:8}}>
                <span style={{fontSize:28}}>{country.flag}</span>
                <div>
                  <h2 style={{color:"#f1f5f9",fontSize:18,fontWeight:800,fontFamily:"Georgia,serif",margin:0}}>{country.country}</h2>
                  <div style={{color:"#64748b",fontSize:12,marginTop:2}}>Select your location to see local candidates</div>
                </div>
              </div>
            </div>

            <div style={{display:"flex",flexDirection:"column",gap:14,marginBottom:20}}>
              <div>
                <div style={{fontSize:10,fontWeight:700,letterSpacing:"0.1em",color:"#64748b",textTransform:"uppercase",marginBottom:8}}>{country.stateLabel}</div>
                <div style={{position:"relative"}}>
                  <select className="qm-sel" value={state} onChange={e => { setState(e.target.value); setLga(""); setErr(""); }}>
                    <option value="">Select {country.stateLabel}...</option>
                    {stateList.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <span style={{position:"absolute",right:13,top:"50%",transform:"translateY(-50%)",color:"#475569",pointerEvents:"none",fontSize:11}}>&#x25BE;</span>
                </div>
              </div>

              {state && lgaList.length > 0 && (
                <div style={{animation:"fadeUp .25s ease"}}>
                  <div style={{fontSize:10,fontWeight:700,letterSpacing:"0.1em",color:"#64748b",textTransform:"uppercase",marginBottom:8}}>{country.lgaLabel}</div>
                  <div style={{position:"relative"}}>
                    <select className="qm-sel" value={lga} onChange={e => { setLga(e.target.value); setErr(""); }}>
                      <option value="">Select {country.lgaLabel}...</option>
                      {lgaList.map(l => <option key={l} value={l}>{l}</option>)}
                    </select>
                    <span style={{position:"absolute",right:13,top:"50%",transform:"translateY(-50%)",color:"#475569",pointerEvents:"none",fontSize:11}}>&#x25BE;</span>
                  </div>
                </div>
              )}

              {/* VOTER-5: Optional gender select */}
              <div>
                <div style={{fontSize:10,fontWeight:700,letterSpacing:"0.1em",color:"#64748b",textTransform:"uppercase",marginBottom:8}}>
                  Gender <span style={{fontWeight:400,textTransform:"none",letterSpacing:0}}>(optional)</span>
                </div>
                <div style={{position:"relative"}}>
                  <select className="qm-sel" value={gender} onChange={e => setGender(e.target.value)}>
                    <option value="">Prefer not to say</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </select>
                  <span style={{position:"absolute",right:13,top:"50%",transform:"translateY(-50%)",color:"#475569",pointerEvents:"none",fontSize:11}}>&#x25BE;</span>
                </div>
              </div>
            </div>

            {/* LEGAL-1: Age verification + LEGAL-3: Policy consent */}
            <div style={{display:"flex",flexDirection:"column",gap:12,marginBottom:16,padding:"14px 12px",background:"#0b1424",border:"1px solid #1e2d45",borderRadius:10}}>
              <label style={{display:"flex",gap:10,cursor:"pointer",fontSize:13,color:"#94a3b8",lineHeight:1.5,alignItems:"flex-start"}}>
                <input
                  type="checkbox"
                  checked={ageConfirmed}
                  onChange={e => { setAgeConfirmed(e.target.checked); setErr(""); }}
                  style={{marginTop:3,accentColor:"#3b82f6",flexShrink:0}}
                />
                <span>I confirm that I am <strong style={{color:"#e2e8f0"}}>18 years or older</strong>.</span>
              </label>

              <label style={{display:"flex",gap:10,cursor:"pointer",fontSize:13,color:"#94a3b8",lineHeight:1.5,alignItems:"flex-start"}}>
                <input
                  type="checkbox"
                  checked={policyConsent}
                  onChange={e => { setPolicyConsent(e.target.checked); setErr(""); }}
                  style={{marginTop:3,accentColor:"#3b82f6",flexShrink:0}}
                />
                <span>
                  I agree to the{" "}
                  <a href={`${(import.meta.env.VITE_LANDING_URL ?? "").replace(/\/$/, "")}/privacy`} target="_blank" rel="noopener noreferrer" style={{color:"#3b82f6",textDecoration:"underline"}}>Privacy Policy</a>
                  {" "}and{" "}
                  <a href={`${(import.meta.env.VITE_LANDING_URL ?? "").replace(/\/$/, "")}/terms`} target="_blank" rel="noopener noreferrer" style={{color:"#3b82f6",textDecoration:"underline"}}>Terms of Service</a>.
                </span>
              </label>
            </div>

            {err && <div style={{marginBottom:12,color:"#ef4444",fontSize:12}}>{err}</div>}

            <button
              onClick={handleLocation}
              disabled={!ageConfirmed || !policyConsent}
              style={{width:"100%",padding:"14px",background: (ageConfirmed && policyConsent) ? "linear-gradient(135deg,#16a34a,#15803d)" : "#1e293b",color: (ageConfirmed && policyConsent) ? "#fff" : "#64748b",border:"none",borderRadius:11,fontSize:15,fontWeight:700,cursor:(ageConfirmed && policyConsent) ? "pointer" : "not-allowed",fontFamily:"inherit",transition:"all 0.2s ease"}}
            >
              {saved?.state ? "Confirm Location" : "Show My Candidates"}
            </button>
          </div>
        )}

        <p style={{color:"#334155",fontSize:11,textAlign:"center",marginTop:28,lineHeight:1.6}}>
          Your email is only used for sign-in and is processed in accordance with our Privacy Policy.
        </p>
      </div>
    </div>
  );
}

export default EmailOnboarding;
